// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

import React, { useEffect, useMemo, useState } from 'react'
import { Box, Button, Divider, Typography, useTheme } from '@mui/material';
import { useAppDispatch, useAppSelector } from 'Hooks';
import { useParams } from 'react-router-dom';
import { GetCreditUsageLedgerThunk, GetOrgThunk } from '../adminThunk';
import { SetOrgIdAction } from '../adminActions';
import LoadingSpinner from 'Components/LoadingSpinner';
import SearchField from 'Components/SearchField';
import { CSVLink } from 'react-csv';
import exportIcon from 'Assets/export.png';
import CreditUsageLedgerCard from './creditUsageLedgerCard';
import orderListIcon from 'Assets/order_list_icon.png';
import stylesDef from './styles';
import { RootState } from 'Store';
import { LEDGER_TYPES } from 'Utils/constants';

const Ledger = () => {
  const theme = useTheme();
  const styles = stylesDef(theme);
  const dispatch = useAppDispatch();
  const { orgId } = useParams();

  //redux state
  const { creditUsageLedger } = useAppSelector((state: RootState) => state.admin);

  //state variables
  const [showLoader, setShowLoader] = useState(false);
  
  //useEffects
  useEffect(() => {
    loadData();
  }, []);

  //useMemo
  const creditUsageLedgerCSVData = useMemo(() => {
    return creditUsageLedger.map((item) => ({
      date: item.updatedAt,
      projectName: item.projectName,
      creditUsage: item.creditAcerage,
      type: item.type === LEDGER_TYPES.order ? 'Order Credits' : 'Subscription Purchased',
      remainingAvailableCredits: item.remainingAvailableCredits,
    }));
  }, [creditUsageLedger]);

  const headers = useMemo(() => {
    return [
      { label: 'Date', key: 'date' },
      { label: 'Project Name', key: 'projectName' },
      { label: 'Credit Usage', key: 'creditUsage' },
      { label: 'Type', key: 'type' },
      { label: 'Remaining Available Credits', key: 'remainingAvailableCredits' },
    ];
  }, []);

  //methods
  const loadData = async () => {
    setShowLoader(true);
    if (_ADMIN_) {
      await Promise.all([
        dispatch(GetOrgThunk(orgId)),
        dispatch(SetOrgIdAction(orgId))
      ]);
    }
    await dispatch(GetCreditUsageLedgerThunk());
    setShowLoader(false);
  };

  const loadSearchResults = async (searchQuery: string) => {
    setShowLoader(true);
    await dispatch(GetCreditUsageLedgerThunk(searchQuery));
    setShowLoader(false);
  };

  const getRemainingAvailableCredits = () => {
    const credits = creditUsageLedger.find(item => item.remainingAvailableCredits)
    return credits?.remainingAvailableCredits || 'N/A';
  }

  return (
    <LoadingSpinner loading={showLoader}>
      <Box sx={styles.pageWrapper}>
        <Box sx={styles.itemsWrapper}>
            <Box sx={styles.headerSectionLeft}>
              <Typography variant="h2">Credit Usage Ledger</Typography>
              <SearchField
                sx={styles.icons}
                placeholder="Search"
                debounceTimeout={500}
                onChange={(value) => loadSearchResults(value)}
              />
            </Box>
            <Box sx={styles.headerSectionRight}>
              <Box sx={styles.availableCredits}>
                <Typography variant="h4">{getRemainingAvailableCredits()}</Typography>
                <Typography variant="body1">Available Credits</Typography>
              </Box>
              {creditUsageLedger.length > 0 && <CSVLink
                data={creditUsageLedgerCSVData}
                headers={headers}
                filename={`credit_usage_ledger_${new Date().toISOString().split('T')[0]}.csv`}
                style={styles.exportButton}
                onClick={() => {
                  window.pendo.track('Export Credit Usage Ledger');
                }}
              >
                <img src={exportIcon} alt="export .csv" />
                <Typography variant="h6" sx={styles.exportButtonText}>Export</Typography>
              </CSVLink>}
            </Box>
        </Box>
        <Divider sx={styles.divider} />
        {!showLoader && creditUsageLedger.length > 0 && creditUsageLedger.map((item) => (
          <CreditUsageLedgerCard key={item._id} creditUsageLedger={item} />
        ))}
        {!showLoader && creditUsageLedger.length === 0 && (
          <Box sx={styles.noOrderContainer}>
            <img src={orderListIcon} alt="icon" />
            <Typography variant="h5">There is no credit history to show.</Typography>
          </Box>
        )}
      </Box>
    </LoadingSpinner>
  );
};

export default Ledger;
